<template>
    <!-- 管理员-项目管理/阶段管理 -->
    <div class="stage-manage wrapper" v-loading="loading">
        <proTitle></proTitle>
        <div class="toolbar">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">新增阶段</el-button>
        </div>

        <draggable class="phase-list" v-model="phaseList" handle=".drag-icon" animation="200" @end="onSortEnd">
            <div class="phase-card" :class="{ 'is-disabled': !phase.enabled }" v-for="phase in phaseList"
                :key="phase.id">
                <div class="phase-card-head flex-align-between">
                    <div class="head-left flex-align">
                        <i class="el-icon-rank drag-icon"></i>
                        <i class="el-icon-location pin-icon"></i>
                        <span class="phase-title">{{ phase.displayName }}</span>
                        <el-tag size="mini" :type="phase.enabled ? 'success' : 'info'">
                            {{ phase.enabled ? '已启用' : '已停用' }}
                        </el-tag>
                    </div>
                    <div class="head-actions">
                        <el-button size="mini" plain :loading="phase._toggling" @click="toggleEnable(phase)">
                            {{ phase.enabled ? '取消启用' : '启用' }}
                        </el-button>
                        <el-button size="mini" plain @click="openEdit(phase)">编辑阶段</el-button>
                        <el-button size="mini" type="danger" plain @click="deletePhase(phase)">删除</el-button>
                    </div>
                </div>
                <div class="phase-meta">
                    <p class="meta-line">用户组：{{ phase.userGroupNames.join('、') || '—' }}</p>
                    <template v-if="phase.periods.length">
                        <template v-for="(period, index) in phase.periods">
                            <p class="meta-line" :key="'week' + phase.id + '-' + index">
                                {{ weekLabel(index) }}：{{ period.start }} ~ {{ period.end }}
                            </p>
                            <p class="meta-line is-muted" :key="'attend' + phase.id + '-' + index">
                                考勤：{{ weekAttendanceSummary(phase, period) }}
                            </p>
                        </template>
                    </template>
                    <template v-else>
                        <p class="meta-line is-muted">时间：—</p>
                        <p class="meta-line is-muted">考勤：未配置</p>
                    </template>
                </div>
                <div class="phase-created">创建时间：{{ phase.createdAt || '—' }}</div>
            </div>
        </draggable>
        <el-empty v-if="!loading && !phaseList.length" description="暂无阶段，请点击「新增阶段」创建"></el-empty>

        <!-- 新增/编辑阶段 -->
        <el-dialog :title="isCreating ? '新增阶段' : '编辑阶段'" :visible.sync="dialogVisible" width="680px" top="6vh"
            :close-on-click-modal="false">
            <el-form label-width="90px" size="small" @submit.native.prevent>
                <el-form-item label="阶段名称" required>
                    <el-select v-model="form.type" style="width:170px" @change="phaseTypeChange">
                        <el-option v-for="opt in phaseOptions" :key="opt.type" :label="opt.label"
                            :value="opt.type"></el-option>
                    </el-select>
                    <el-input v-model.trim="form.displayName" placeholder="如：集中培训"
                        style="width:200px;margin-left:10px" maxlength="20"></el-input>
                </el-form-item>
                <el-form-item label="用户组" required>
                    <el-checkbox-group v-model="form.userGroupIds" class="user-group-checkboxes">
                        <el-checkbox v-for="group in userGroupOptions" :key="group.id" :label="group.id">
                            <span class="user-group-label" :title="group.name">{{ group.name }}</span>
                        </el-checkbox>
                    </el-checkbox-group>
                    <div class="form-hint" v-if="userGroupOptions.length">选择本阶段适用的用户组，可多选</div>
                    <div class="form-hint" v-else>当前项目暂无可用用户组，请先在项目中配置</div>
                </el-form-item>
                <el-form-item label="阶段周次">
                    <div class="week-row" v-for="(week, index) in form.weeks" :key="index">
                        <span class="week-label">{{ weekLabel(index) }}</span>
                        <el-date-picker v-model="week.range" type="daterange" value-format="yyyy-MM-dd"
                            range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期"
                            :picker-options="weekPickerOptions(index)" style="width:280px"></el-date-picker>
                        <el-button type="text" class="btn-remove-week" icon="el-icon-close"
                            @click="removeWeek(index)"></el-button>
                    </div>
                    <el-button type="text" icon="el-icon-plus" @click="addWeek">添加一周</el-button>
                    <div class="form-hint">可选范围：项目执行期 {{ projectStart || '—' }} ~ {{ projectEnd || '—' }}；已被其他阶段或其他周占用的日期不可选</div>
                </el-form-item>
                <el-form-item label="考勤配置">
                    <el-button type="text" @click="generateAttendance">根据周次生成</el-button>
                    <div class="form-hint" style="margin-top:0">
                        考勤分为两类：上午为「{{ attendanceName }}考勤」，晚上为「住宿考勤」，各类别分别选择打卡日期与时间段（可不配置）
                    </div>
                    <div class="attendance-block" v-for="type in ['morning', 'evening']" :key="type">
                        <p class="attendance-title">
                            {{ type === 'morning' ? `上午 · ${attendanceName}考勤` : '晚上 · 住宿考勤' }}
                        </p>
                        <div class="attendance-row flex-align">
                            <span class="attendance-label">打卡时间段</span>
                            <el-time-picker v-model="form.attendance[type].timeStart" value-format="HH:mm"
                                format="HH:mm" placeholder="开始" style="width:110px"></el-time-picker>
                            <span class="range-sep">至</span>
                            <el-time-picker v-model="form.attendance[type].timeEnd" value-format="HH:mm" format="HH:mm"
                                placeholder="结束" style="width:110px"></el-time-picker>
                            <span class="attendance-count">已选 {{ form.attendance[type].dates.length }} 天</span>
                        </div>
                        <div class="attendance-row flex-align">
                            <span class="attendance-label">打卡日期</span>
                            <el-date-picker v-model="form.attendance[type].dates" type="dates" value-format="yyyy-MM-dd"
                                placeholder="选择打卡日期（可多选）" :picker-options="attendancePickerOptions"
                                style="width:320px"></el-date-picker>
                        </div>
                    </div>
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button size="small" @click="dialogVisible = false">取消</el-button>
                <el-button size="small" type="primary" :loading="saving" @click="savePhase">保存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import draggable from 'vuedraggable';
    import { mapState } from 'vuex';

    const PHASE_OPTIONS = [
        { type: 1, label: '阶段一-集中', displayName: '集中培训' },
        { type: 2, label: '阶段二-跟岗', displayName: '跟岗学习' },
        { type: 3, label: '阶段三-返岗', displayName: '返岗实践' },
    ];

    const datesInRange = (start, end) => {
        let dates = [];
        let current = new Date(start.replace(/-/g, '/'));
        let last = new Date(end.replace(/-/g, '/'));
        while (current <= last) {
            let m = String(current.getMonth() + 1).padStart(2, '0');
            let d = String(current.getDate()).padStart(2, '0');
            dates.push(`${current.getFullYear()}-${m}-${d}`);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    const toDateStr = (val) => {
        if (!val) return '';
        if (typeof val === 'string') return val.slice(0, 10);
        let d = new Date(val);
        if (Number.isNaN(d.getTime())) return '';
        let m = String(d.getMonth() + 1).padStart(2, '0');
        let day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${m}-${day}`;
    };

    // put 返回完整 axios response，get/post 返回 body
    const unwrapApi = (res) => {
        if (!res) return res;
        if (res.status != null && res.data && typeof res.data === 'object' && 'code' in res.data) {
            return res.data;
        }
        return res;
    };

    const isApiSuccess = (res) => {
        let body = unwrapApi(res);
        return body && (body.code === 0 || body.code === 200);
    };

    const apiMsg = (res) => {
        let body = unwrapApi(res);
        return (body && body.msg) || '';
    };

    const emptyAttendance = () => ({
        morning: { timeStart: '08:00', timeEnd: '09:30', dates: [] },
        evening: { timeStart: '20:30', timeEnd: '22:00', dates: [] },
    });

    export default {
        components: { draggable },
        data() {
            return {
                loading: false,
                saving: false,
                projectStart: '',
                projectEnd: '',
                phaseOptions: PHASE_OPTIONS,
                userGroupOptions: [],
                phaseList: [],
                dialogVisible: false,
                isCreating: false,
                editingId: null,
                lastType: null,
                form: {
                    type: 1,
                    displayName: '',
                    userGroupIds: [],
                    weeks: [],
                    attendance: emptyAttendance(),
                },
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            attendanceName() {
                if (this.form.displayName) return this.form.displayName;
                let opt = this.phaseOptions.find(item => item.type === this.form.type);
                return opt ? opt.displayName : '阶段';
            },
            attendancePickerOptions() {
                let allowedDates = new Set();
                this.form.weeks.forEach(week => {
                    if (week.range && week.range.length === 2 && week.range[0] && week.range[1]) {
                        let dates = datesInRange(week.range[0], week.range[1]);
                        dates.forEach(d => allowedDates.add(d));
                    }
                });
                return {
                    disabledDate(date) {
                        let m = String(date.getMonth() + 1).padStart(2, '0');
                        let d = String(date.getDate()).padStart(2, '0');
                        let str = `${date.getFullYear()}-${m}-${d}`;
                        return !allowedDates.has(str);
                    },
                };
            },
            projectPickerOptions() {
                let start = this.projectStart;
                let end = this.projectEnd;
                return {
                    disabledDate(date) {
                        if (!start || !end) return false;
                        let m = String(date.getMonth() + 1).padStart(2, '0');
                        let d = String(date.getDate()).padStart(2, '0');
                        let str = `${date.getFullYear()}-${m}-${d}`;
                        return str < start || str > end;
                    },
                };
            },
        },
        watch: {
            projectId: {
                immediate: true,
                handler(id) {
                    if (id) this.loadAll();
                    else {
                        this.phaseList = [];
                        this.userGroupOptions = [];
                    }
                },
            },
        },
        methods: {
            weekLabel(index) {
                const cn = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
                return `第${cn[index] || index + 1}周`;
            },
            weekPickerOptions(currentIndex) {
                let start = this.projectStart;
                let end = this.projectEnd;
                let occupied = [];
                // 本阶段其他周已占用
                this.form.weeks.forEach((week, i) => {
                    if (i === currentIndex) return;
                    if (week.range && week.range.length === 2 && week.range[0] && week.range[1]) {
                        occupied.push({ start: week.range[0], end: week.range[1] });
                    }
                });
                // 其他阶段已占用（编辑时排除当前阶段）
                this.phaseList.forEach(phase => {
                    if (this.editingId && phase.id === this.editingId) return;
                    (phase.periods || []).forEach(period => {
                        if (period.start && period.end) {
                            occupied.push({ start: period.start, end: period.end });
                        }
                    });
                });
                return {
                    disabledDate(date) {
                        let m = String(date.getMonth() + 1).padStart(2, '0');
                        let d = String(date.getDate()).padStart(2, '0');
                        let str = `${date.getFullYear()}-${m}-${d}`;
                        if (start && end && (str < start || str > end)) return true;
                        for (let range of occupied) {
                            if (str >= range.start && str <= range.end) return true;
                        }
                        return false;
                    },
                };
            },
            weekAttendanceSummary(phase, period) {
                let weekDates = datesInRange(period.start, period.end);
                let daySet = {};
                let times = 0;
                ['morning', 'evening'].forEach(type => {
                    (phase.attendance[type].dates || []).forEach(date => {
                        if (weekDates.indexOf(date) === -1) return;
                        daySet[date] = true;
                        times += 1;
                    });
                });
                let dayCount = Object.keys(daySet).length;
                return times ? `${dayCount}天·${times}次` : '未配置';
            },
            defaultDisplayName(type) {
                let opt = this.phaseOptions.find(item => item.type === type);
                return opt ? opt.displayName : '';
            },
            mapStageItem(vo) {
                let groups = Array.isArray(vo.userGroups) ? vo.userGroups : [];
                let userGroupIds = Array.isArray(vo.userGroupIds) && vo.userGroupIds.length
                    ? vo.userGroupIds.map(Number)
                    : groups.map(g => Number(g.id));
                let periods = (vo.periods || []).map(p => ({
                    cycleId: p.cycleId,
                    cycleNumber: p.cycleNumber,
                    cycleName: p.cycleName,
                    start: p.startDate,
                    end: p.endDate,
                }));
                return {
                    id: vo.id,
                    type: Number(vo.type),
                    typeLabel: vo.typeLabel || '',
                    displayName: vo.stageName || '',
                    enabled: Number(vo.enabled) === 1,
                    createdAt: toDateStr(vo.createdAt),
                    userGroupIds,
                    userGroupNames: groups.map(g => g.name).filter(Boolean),
                    periods,
                    attendance: {
                        morning: {
                            timeStart: vo.morningStart || '08:00',
                            timeEnd: vo.morningEnd || '09:30',
                            dates: (vo.morningDates || []).slice(),
                        },
                        evening: {
                            timeStart: vo.stayStart || '20:30',
                            timeEnd: vo.stayEnd || '22:00',
                            dates: (vo.stayDates || []).slice(),
                        },
                    },
                    trackDates: (vo.trackDates || []).slice(),
                };
            },
            async loadAll() {
                if (!this.projectId) return;
                this.loading = true;
                try {
                    await Promise.all([this.fetchProjectInfo(), this.fetchStageList()]);
                } finally {
                    this.loading = false;
                }
            },
            async fetchProjectInfo() {
                let res = await this.$Api.StageV2.getProjectInfo({ projectId: this.projectId });
                if (!isApiSuccess(res)) {
                    this.$message.error(apiMsg(res) || '获取项目信息失败');
                    return;
                }
                let data = res.data || {};
                this.projectStart = toDateStr(data.periodStartTime);
                this.projectEnd = toDateStr(data.periodFinishTime);
                this.userGroupOptions = (data.availableUserGroups || []).map(g => ({
                    id: Number(g.id),
                    name: g.name,
                }));
            },
            async fetchStageList() {
                let res = await this.$Api.StageV2.getStageList({ projectId: this.projectId });
                if (!isApiSuccess(res)) {
                    this.$message.error(apiMsg(res) || '获取阶段列表失败');
                    this.phaseList = [];
                    return;
                }
                this.phaseList = (res.data || []).map(item => this.mapStageItem(item));
            },
            phaseTypeChange(value) {
                if (!this.form.displayName || this.form.displayName === this.defaultDisplayName(this.lastType)) {
                    this.form.displayName = this.defaultDisplayName(value);
                }
                this.lastType = value;
            },
            addWeek() {
                this.form.weeks.push({ range: [], cycleId: null });
            },
            removeWeek(index) {
                if (this.form.weeks.length <= 1) {
                    this.$message.warning('至少保留一周');
                    return;
                }
                this.form.weeks.splice(index, 1);
            },
            generateAttendance() {
                let weeks = this.form.weeks.filter(week => week.range && week.range.length === 2);
                if (!weeks.length) {
                    this.$message.warning('请先完整配置阶段周次，再生成考勤日期');
                    return;
                }
                let dateMap = {};
                weeks.forEach(week => {
                    datesInRange(week.range[0], week.range[1]).forEach(date => (dateMap[date] = true));
                });
                let dates = Object.keys(dateMap).sort();
                this.form.attendance.morning.dates = dates.slice();
                this.form.attendance.evening.dates = dates.slice();
            },
            openCreate() {
                this.isCreating = true;
                this.editingId = null;
                let type = 1;
                this.form = {
                    type,
                    displayName: this.defaultDisplayName(type),
                    userGroupIds: this.userGroupOptions.map(g => g.id),
                    weeks: [{ range: [], cycleId: null }],
                    attendance: emptyAttendance(),
                };
                this.lastType = type;
                this.dialogVisible = true;
            },
            openEdit(phase) {
                this.isCreating = false;
                this.editingId = phase.id;
                this.form = {
                    type: phase.type,
                    displayName: phase.displayName,
                    userGroupIds: phase.userGroupIds.slice(),
                    weeks: phase.periods.length
                        ? phase.periods.map(period => ({
                            range: [period.start, period.end],
                            cycleId: period.cycleId || null,
                        }))
                        : [{ range: [], cycleId: null }],
                    attendance: {
                        morning: {
                            timeStart: phase.attendance.morning.timeStart,
                            timeEnd: phase.attendance.morning.timeEnd,
                            dates: phase.attendance.morning.dates.slice(),
                        },
                        evening: {
                            timeStart: phase.attendance.evening.timeStart,
                            timeEnd: phase.attendance.evening.timeEnd,
                            dates: phase.attendance.evening.dates.slice(),
                        },
                    },
                };
                this.lastType = phase.type;
                this.dialogVisible = true;
            },
            buildSavePayload() {
                let periods = [];
                for (let i = 0; i < this.form.weeks.length; i++) {
                    let week = this.form.weeks[i];
                    let range = week.range;
                    if (!range || range.length !== 2) {
                        this.$message.warning(`请完整填写${this.weekLabel(i)}的起止日期`);
                        return null;
                    }
                    // 本阶段周次之间不可重叠
                    for (let j = 0; j < i; j++) {
                        let prev = this.form.weeks[j].range;
                        if (prev && prev.length === 2 && range[0] <= prev[1] && range[1] >= prev[0]) {
                            this.$message.warning(`${this.weekLabel(i)}与${this.weekLabel(j)}的日期存在重叠`);
                            return null;
                        }
                    }
                    // 不可与其他阶段已选日期重叠
                    for (let phase of this.phaseList) {
                        if (this.editingId && phase.id === this.editingId) continue;
                        for (let period of (phase.periods || [])) {
                            if (!period.start || !period.end) continue;
                            if (range[0] <= period.end && range[1] >= period.start) {
                                this.$message.warning(`${this.weekLabel(i)}与「${phase.displayName}」的日期存在重叠`);
                                return null;
                            }
                        }
                    }
                    let item = {
                        cycleNumber: i + 1,
                        cycleName: this.weekLabel(i),
                        startDate: range[0],
                        endDate: range[1],
                    };
                    if (week.cycleId) item.cycleId = week.cycleId;
                    periods.push(item);
                }
                let morning = this.form.attendance.morning;
                let evening = this.form.attendance.evening;
                return {
                    id: this.isCreating ? null : this.editingId,
                    enabled: this.isCreating ? 0 : undefined,
                    projectId: this.projectId,
                    stageName: this.form.displayName,
                    type: this.form.type,
                    userGroupIds: this.form.userGroupIds.map(Number),
                    morningStart: morning.timeStart || null,
                    morningEnd: morning.timeEnd || null,
                    stayStart: evening.timeStart || null,
                    stayEnd: evening.timeEnd || null,
                    periods,
                    morningDates: (morning.dates || []).slice().sort(),
                    stayDates: (evening.dates || []).slice().sort(),
                    trackDates: [],
                };
            },
            async savePhase() {
                if (!this.form.displayName) {
                    this.$message.warning('请填写阶段名称');
                    return;
                }
                if (!this.form.userGroupIds.length) {
                    this.$message.warning('请至少选择一个用户组');
                    return;
                }
                if (!this.projectId) {
                    this.$message.warning('未获取到当前项目');
                    return;
                }
                let payload = this.buildSavePayload();
                if (!payload) return;
                this.saving = true;
                try {
                    let res = await this.$Api.StageV2.saveStage(payload);
                    if (isApiSuccess(res)) {
                        this.$message.success('保存成功');
                        this.dialogVisible = false;
                        await this.fetchStageList();
                    } else {
                        this.$message.error(apiMsg(res) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.saving = false;
                }
            },
            async toggleEnable(phase) {
                let next = phase.enabled ? 0 : 1;
                this.$set(phase, '_toggling', true);
                try {
                    let res = await this.$Api.StageV2.setStageEnabled({
                        stageId: phase.id,
                        enabled: next,
                    });
                    if (isApiSuccess(res)) {
                        phase.enabled = next === 1;
                        this.$message.success(next === 1 ? '已启用' : '已取消启用');
                    } else {
                        this.$message.error(apiMsg(res) || '操作失败');
                    }
                } catch (e) {
                    this.$message.error('操作失败');
                } finally {
                    this.$set(phase, '_toggling', false);
                }
            },
            deletePhase(phase) {
                this.$confirm(`确定删除「${phase.displayName}」？`, '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                }).then(async () => {
                    try {
                        let res = await this.$Api.StageV2.deleteStage({ stageId: phase.id });
                        if (isApiSuccess(res)) {
                            this.$message.success('删除成功');
                            await this.fetchStageList();
                        } else {
                            this.$message.error(apiMsg(res) || '删除失败');
                        }
                    } catch (e) {
                        this.$message.error('删除失败');
                    }
                }).catch(() => {});
            },
            async onSortEnd() {
                if (!this.projectId || !this.phaseList.length) return;
                try {
                    let res = await this.$Api.StageV2.sortStages({
                        projectId: this.projectId,
                        stageIds: this.phaseList.map(item => item.id),
                    });
                    if (!isApiSuccess(res)) {
                        this.$message.error(apiMsg(res) || '排序失败');
                        await this.fetchStageList();
                    }
                } catch (e) {
                    this.$message.error('排序失败');
                    await this.fetchStageList();
                }
            },
        },
    };
</script>

<style lang="less" scoped>
    .stage-manage {
        padding: 20px 25px;
        box-sizing: border-box;

        .toolbar {
            margin-bottom: 16px;
        }

        .phase-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .phase-card {
            position: relative;
            background: #fff;
            border: 1px solid #93C5FD;
            border-radius: 10px;
            padding: 16px 18px 36px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

            &.is-disabled {
                opacity: 0.72;
                border-color: #D1D5DB;
                background: #FAFAFA;
            }

            .head-left {
                .drag-icon {
                    color: #C0C4CC;
                    margin-right: 8px;
                    cursor: move;
                }

                .pin-icon {
                    color: #EC4899;
                    margin-right: 8px;
                }

                .phase-title {
                    font-size: 15px;
                    font-weight: 600;
                    color: #303133;
                    margin-right: 8px;
                }
            }

            .phase-meta {
                margin-top: 10px;

                .meta-line {
                    font-size: 13px;
                    color: #606266;
                    line-height: 1.9;

                    &.is-muted {
                        color: #909399;
                    }
                }
            }

            .phase-created {
                position: absolute;
                right: 18px;
                bottom: 12px;
                font-size: 12px;
                color: #C0C4CC;
            }
        }

        .user-group-checkboxes {
            .user-group-label {
                display: inline-block;
                max-width: 160px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                vertical-align: bottom;
            }
        }

        .form-hint {
            font-size: 12px;
            color: #909399;
            line-height: 1.6;
            margin-top: 4px;
        }

        .week-row {
            margin-bottom: 8px;

            .week-label {
                display: inline-block;
                width: 45px;
                font-size: 13px;
                color: #606266;
            }

            .btn-remove-week {
                margin-left: 8px;
                color: #909399;

                &:hover {
                    color: #F56C6C;
                }
            }
        }

        .attendance-block {
            border: 1px solid #EBEEF5;
            border-radius: 6px;
            padding: 10px 14px;
            margin-top: 10px;

            .attendance-title {
                font-size: 13px;
                font-weight: 600;
                color: #303133;
                margin-bottom: 8px;
            }

            .attendance-row {
                margin-bottom: 8px;

                &:last-child {
                    margin-bottom: 0;
                }

                .attendance-label {
                    width: 75px;
                    font-size: 13px;
                    color: #606266;
                    flex-shrink: 0;
                }

                .range-sep {
                    margin: 0 8px;
                    color: #909399;
                }

                .attendance-count {
                    margin-left: 12px;
                    font-size: 12px;
                    color: #909399;
                }
            }
        }
    }

    /deep/ .el-dialog__title {
        font-weight: bold;
    }

    /deep/ .el-form-item__label {
        font-weight: bold;
    }
</style>
